import React, { useMemo } from 'react';
import ReactEcharts from 'echarts-for-react';
import type { EChartsOption } from 'echarts';
import { YearlyPriceData } from './types';

interface PricePredictionChartProps {
  type?: 'stock' | 'crypto';
  predictionData: { [year: number]: YearlyPriceData };
}

const generateAutoTicks = (values, tickCount = 5, symbolType) => {
  const nums = values.map(Number);

  const min = Math.min(...nums);
  const max = Math.max(...nums);

  const range = max - min;
  const ratio = max / min;

  let type = 'value';
  let ticks: number[] = [];
  const useLog = ratio > 20 && min > 20;

  if (useLog && symbolType !== 'stock') {
    type = 'log';
    const logMin = Math.log10(min);
    const logMax = Math.log10(max);
    const step = (logMax - logMin) / (tickCount - 1);

    for (let i = 0; i < tickCount; i++) {
      let v = Math.pow(10, logMin + i * step);
      v = Number(v.toPrecision(2));
      ticks.push(v);
    }
  } else if (range / tickCount < 1e-3) {
    type = 'value';
    const padding = range === 0 ? min * 0.1 || 0.1 : range * 0.5;
    let lower = min - padding;
    const upper = max + padding;

    if (lower < 0) lower = 0;

    ticks = [lower, min, max, upper];
  } else {
    type = 'value';
    const step = range / (tickCount - 1);
    for (let i = 0; i < tickCount; i++) {
      let v = min + step * i;
      const magnitude = Math.pow(10, Math.floor(Math.log10(range)));
      v = (Math.round((v / magnitude) * 10) / 10) * magnitude;
      ticks.push(Number(v.toPrecision(3)));
    }
  }

  return {
    max: ticks[ticks.length - 1],
    maxDecimal: max < 0.0001 ? 8 : 2,
    min: ticks[0],
    ticks,
    type,
  };
};

const formatCurrencyNumberForChart = (v: string) => Number(v.replace(/[$,]/g, ''));

export const PricePredictionChart: React.FC<PricePredictionChartProps> = ({ predictionData, type = 'stock' }) => {
  let predictionsYears: number[] = [];
  let bullishData: number[] = [];
  let averageData: number[] = [];
  let bearishData: number[] = [];

  predictionsYears = Object.keys(predictionData).map(yearKey => parseInt(yearKey, 10));

  bullishData = predictionsYears.map(y => formatCurrencyNumberForChart(predictionData[y].max));
  averageData = predictionsYears.map(y => formatCurrencyNumberForChart(predictionData[y].avg));
  bearishData = predictionsYears.map(y => formatCurrencyNumberForChart(predictionData[y].min));

  if (type === 'crypto') {
    predictionsYears = predictionsYears.filter(y => y <= 2030);
    bullishData = bullishData.slice(0, predictionsYears.length);
    averageData = averageData.slice(0, predictionsYears.length);
    bearishData = bearishData.slice(0, predictionsYears.length);
  }

  const allValues = [...bullishData.map(Number), ...averageData.map(Number), ...bearishData.map(Number)];

  const values = allValues.map(Number);

  const yAxisConfig = useMemo(() => generateAutoTicks(values, 5, type), [values, type]);

  const customOptions = {
    grid: {
      bottom: '6%',
      containLabel: true,
      left: '1%',
      right: '2%',
      top: '2%',
    },
    legend: {
      bottom: '-1%',
      data: ['Bullish', 'Average', 'Bearish'],
      left: 'center',
      orient: 'horizontal',
      textStyle: {
        fontFamily: 'Manrope, sans-serif',
        fontWeight: 600,
      },
    },
    series: [
      {
        color: '#30A851',
        data: bullishData,
        name: 'Bullish',
        type: 'line',
      },
      {
        color: '#99A82F',
        data: averageData,
        name: 'Average',
        type: 'line',
      },
      {
        color: '#A25121',
        data: bearishData,
        name: 'Bearish',
        type: 'line',
      },
    ],
    title: {
      text: '',
      textStyle: {
        fontFamily: 'Manrope, sans-serif',
        fontSize: 20,
        fontWeight: 'bold',
      },
    },
    toolbox: {},
    tooltip: {
      formatter: function (params: any) {
        let tooltip = params[0].axisValueLabel + '<br/>';
        params.forEach((item: any) => {
          tooltip +=
            item.marker +
            ' ' +
            item.seriesName +
            ': $' +
            parseFloat(item.data).toLocaleString(undefined, {
              maximumFractionDigits: 8,
              minimumFractionDigits: 2,
            }) +
            '<br/>';
        });
        return tooltip;
      },
      textStyle: {
        fontFamily: 'Manrope, sans-serif',
        fontWeight: 500,
      },
      trigger: 'axis',
    },
    xAxis: {
      axisLabel: {
        fontFamily: 'Manrope, sans-serif',
        fontWeight: 'bold',
      },
      boundaryGap: false,
      data: predictionsYears,
      type: 'category',
    },
    yAxis: {
      axisLabel: {
        fontFamily: 'Manrope, sans-serif',
        fontWeight: 'bold',
        formatter: function (value) {
          return '$' + Number(value).toFixed(yAxisConfig.maxDecimal);
        },
      },
      axisTick: { show: true, values: yAxisConfig.ticks },
      max: yAxisConfig.max,
      min: yAxisConfig.min,
      type: yAxisConfig.type,
    },
  } as EChartsOption;

  return <ReactEcharts option={customOptions || {}} style={{ height: '450px', overflow: 'hidden', width: '100%' }} />;
};
